import {HTMLAttributes, ReactNode, useCallback} from 'react';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import clsx from 'clsx';
import pointerWithinVertical from './utils';

type SortableVerticalProps<T extends {id: string}> = {
    items: T[];
    onSortChange: (items: T[]) => void;
    renderItem: (item: T, index: number) => ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const SortableVertical = <T extends {id: string}>({
    className,
    items,
    onSortChange,
    renderItem,
    ...divProps
}: SortableVerticalProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const {active, over} = event;

            if (over === null) {
                return;
            }

            if (active.id !== over.id) {
                const oldIndex = items.findIndex(
                    (item) => item.id === active.id,
                );
                const newIndex = items.findIndex((item) => item.id === over.id);

                onSortChange(arrayMove(items, oldIndex, newIndex));
            }
        },
        [items, onSortChange],
    );

    return (
        <div className={clsx('flex flex-col', className)} {...divProps}>
            <DndContext
                collisionDetection={pointerWithinVertical}
                measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
                sensors={sensors}
            >
                <SortableContext
                    items={items}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map(renderItem)}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default SortableVertical;
